import { Component, ChangeDetectionStrategy, input, computed } from '@angular/core';

/**
 * Table skeleton loading component.
 * Displays a simplified shimmer placeholder while table data is loading.
 * First row has 3 cells (varied widths), remaining rows have 2 cells.
 * Supports dark mode and respects prefers-reduced-motion.
 */
@Component({
  selector: 'app-table-skeleton',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="skeleton-table" role="status" aria-label="Loading table data">
      <!-- First row: slightly more detailed -->
      <div class="skeleton-row skeleton-row--first">
        <div class="skeleton-cell" style="flex: 2"></div>
        <div class="skeleton-cell" style="flex: 3"></div>
        <div class="skeleton-cell" style="flex: 1"></div>
      </div>
      <!-- Remaining rows: simplified -->
      @for (_ of restRowsArray(); track $index) {
        <div class="skeleton-row">
          <div class="skeleton-cell" style="flex: 2"></div>
          <div class="skeleton-cell" style="flex: 4"></div>
        </div>
      }
      <span class="visually-hidden">Loading...</span>
    </div>
  `,
  styles: [`
    .skeleton-table {
      width: 100%;
      padding: var(--space-md, 16px);
    }

    .skeleton-row {
      display: flex;
      gap: var(--space-lg, 24px);
      padding: var(--space-md, 16px) 0;
      border-bottom: 1px solid var(--gokuri-border-light, #f5f5f5);
    }

    .skeleton-row--first {
      padding-bottom: var(--space-lg, 24px);
    }

    .skeleton-cell {
      height: 14px;
      min-width: 0;
      border-radius: var(--radius-sm, 4px);
      background: linear-gradient(
        90deg,
        var(--gokuri-skeleton-base, #f0f0f0) 25%,
        var(--gokuri-skeleton-highlight, #e8e8e8) 50%,
        var(--gokuri-skeleton-base, #f0f0f0) 75%
      );
      background-size: 200% 100%;
      animation: shimmer var(--gokuri-skeleton-animation-duration, 1.5s) ease-in-out infinite;
    }

    @keyframes shimmer {
      0% { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }

    @media (prefers-reduced-motion: reduce) {
      .skeleton-cell {
        animation: none;
        background: var(--gokuri-skeleton-base, #f0f0f0);
      }
    }

    .visually-hidden {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    }
  `]
})
export class TableSkeletonComponent {
  /** Total number of skeleton rows to display */
  readonly rows = input(6);

  /** Array for remaining rows (total - 1 for the first row) */
  protected restRowsArray = computed(() => Array(Math.max(0, this.rows() - 1)));
}
